/**
 * RBAC permission utility for the frontend.
 *
 * Usage:
 *   import { can } from '../utils/permissions';
 *   can(user, 'loans.create')      // nested new schema
 *   can(user, 'routes.balanceSheet')
 *   can(user, 'canEditLoans')      // legacy flat schema (backward compat)
 */

export const PRESET_LABELS = {
  BACK_OFFICE: "Back Office",
  MANAGEMENT: "Management",
  ADMIN: "Admin",
  CUSTOM: "Custom",
};

/**
 * Resolve a dot-notation key path through an object.
 * Returns the value at that path, or undefined if not found.
 */
function resolvePath(obj, path) {
  const parts = path.split(".");
  let cursor = obj;
  for (const part of parts) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = cursor[part];
  }
  return cursor;
}

/**
 * Check whether `user` has a given permission.
 *
 * @param {object} user  - The user object from auth state (includes role, permissions)
 * @param {string} key   - Dot-notation permission key, e.g. 'loans.create' or 'routes.loans'
 *                         Legacy flat keys like 'canEditLoans' also work.
 * @returns {boolean}
 */
export function can(user, key) {
  if (!user) return false;

  // Admins always have all permissions
  if (user.role === "ADMIN") return true;

  const perms = user.permissions;
  if (!perms) return false;

  // Detect whether this is the new nested schema or the old flat schema.
  // The new schema has a `routes` key at the top level.
  const isNewSchema = perms.routes !== undefined;

  if (isNewSchema) {
    // Dot-notation resolution for new nested schema
    return Boolean(resolvePath(perms, key));
  }

  // Legacy flat schema fallback (e.g. canEditLoans, canViewLoans)
  // Map common new-style keys to old flat booleans gracefully
  const legacyMap = {
    "routes.loans": "canViewLoans",
    "routes.loanDetail": "canViewLoans",
    "routes.collateral": "canViewLoans",
    "routes.creditFacilities": "canViewLoans",
    "routes.balanceSheet": "canViewLoans",
    "routes.incomeStatement": "canViewLoans",
    "routes.cashFlow": "canViewLoans",
    "routes.financials": "canViewLoans",
    "routes.dashboard": "canViewLoans",
    "routes.userManagement": "canManageUsers",
    "routes.activityLog": "canManageUsers",
    "loans.create": "canEditLoans",
    "loans.edit": "canEditLoans",
    "loans.close": "canEditLoans",
    "loans.hardDelete": "canEditLoans",
  };

  // If a dot-key has a legacy mapping, use that
  if (key.includes(".") && legacyMap[key] !== undefined) {
    return Boolean(perms[legacyMap[key]]);
  }

  // Direct flat key lookup (e.g. 'canEditLoans')
  return Boolean(perms[key]);
}
